import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Stack from '@mui/material/Stack';
import { getApiKeys, updateApiKey, deleteApiKey } from '../../apis/apiKeys';
import { toast } from 'react-toastify';
import { TextField, InputAdornment, Switch, IconButton, Tooltip, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Add, ContentCopy, DeleteForeverOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { first, includes, isEmpty } from 'lodash';
import CreateAPIDialog from './CreateAPIDialog';
import Loader from '../loader/Loader';
import ConfirmationDialog from './ConfirmationDialog';
import { formatDate } from '../../lib/utils';
import TablePagination from '../TablePagination';

const DashboardAPIKeys = () => {
    const permissions = useSelector((state) => state.auth.permissions);

    const [openApi, setOpenApi] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [apiKeys, setApiKeys] = useState([]);
    const [viewKeyId, setViewKeyId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortModel, setSortModel] = React.useState(null);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        type: 'warning',
        title: '',
        message: '',
        onConfirm: null,
        loading: false,
    });

    const fetchKeys = async () => {
        if (!isPermitted('list')) return;

        setLoading(true);
        setError(null);
        try {
            const data = await getApiKeys(sortModel);
            setApiKeys(data);
        } catch {
            setError('Failed to load API keys');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchKeys();
    }, [sortModel]);

    const handleDeleteKey = (id) => {
        setConfirmDialog({
            open: true,
            type: 'danger',
            title: 'Delete API Key',
            message: 'Are you sure you want to delete this API key permanently?',
            onConfirm: async () => {
                setConfirmDialog((prev) => ({ ...prev, loading: true }));
                try {
                    await deleteApiKey(id);
                    toast.success('API key deleted successfully');
                    fetchKeys();
                    setConfirmDialog({
                        open: false,
                        type: 'warning',
                        title: '',
                        message: '',
                        onConfirm: null,
                        loading: false,
                    });
                } catch {
                    toast.error('Failed to delete API key');
                    setConfirmDialog((prev) => ({ ...prev, loading: false }));
                }
            },
            loading: false,
        });
    };

    const handleCopy = (key) => {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard
                .writeText(key)
                .then(() => toast.success('Copied to clipboard'))
                .catch(() => toast.error('Failed to copy'));
        } else {
            try {
                const textarea = document.createElement('textarea');
                textarea.value = key;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                toast.success('Copied to clipboard');
            } catch {
                toast.error('Clipboard not supported');
            }
        }
    };

    const handleView = (id) => {
        setConfirmDialog({
            open: true,
            type: 'info',
            title: 'View API Key',
            message: 'Are you sure you want to view the full API key? This key is sensitive and should be kept secure.',
            onConfirm: () => {
                setViewKeyId(id);
                setConfirmDialog({ open: false, type: 'warning', title: '', message: '', onConfirm: null, loading: false });
            },
            loading: false,
        });
    };

    const handleHide = () => setViewKeyId(null);

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            await updateApiKey(id, { status: currentStatus === 'active' ? 'inactive' : 'active' });
            toast.success('Status updated successfully');
            fetchKeys();
        } catch {
            toast.error('Failed to update status');
        }
    };

    // Filter rows for DataGrid
    const filteredKeys = apiKeys.filter(
        (key) =>
            key.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            key.host?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalCount = filteredKeys.length;

    // Apply client-side pagination
    const paginatedKeys = filteredKeys.slice(
        paginationModel.page * paginationModel.pageSize,
        (paginationModel.page + 1) * paginationModel.pageSize
    );

    const apirows = paginatedKeys.map((key) => ({
        id: key.id,
        name: key.name || '-',
        key:
            viewKeyId === key.id
                ? key.key
                : '*'.repeat(Math.max(0, (key.key?.length || 0) - 4)) + (key.key?.slice(-4) || ''),
        host: key.host || '-',
        created_at: formatDate(key.created_at),
        status: key.status,
        fullKey: key.key,
        originalStatus: key.status,
    }));

    const isPermitted = (value) => includes(permissions?.expose_api, value);

    // DataGrid columns
    const apicolumns = [
        { field: 'name', headerName: 'API Name', flex: 1, minWidth: 150 },
        {
            field: 'key',
            headerName: 'API Key',
            flex: 2,
            minWidth: 200,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{params.value}</span>
                    {isPermitted('view') ? (
                        <Tooltip title={viewKeyId === params.row.id ? 'Hide' : 'View'}>
                            <IconButton
                                size="small"
                                onClick={() => (viewKeyId === params.row.id ? handleHide() : handleView(params.row.id))}
                            >
                                {viewKeyId === params.row.id ? (
                                    <VisibilityOffIcon fontSize="small" />
                                ) : (
                                    <VisibilityIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </div>
            ),
        },
        { field: 'host', headerName: 'IP Address', flex: 1, minWidth: 130 },
        { field: 'created_at', headerName: 'Added Date', flex: 1, minWidth: 160 },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Switch
                        checked={params.value === 'active'}
                        disabled={!isPermitted('change')}
                        onChange={() => handleToggleStatus(params.row.id, params.row.originalStatus)}
                        size="small"
                        sx={{
                            padding: 0,
                            width: 36,
                            height: 20,
                            '& .MuiSwitch-switchBase': {
                                padding: '2px',
                                '&.Mui-checked': {
                                    transform: 'translateX(16px)',
                                    color: '#fff',
                                    '& + .MuiSwitch-track': { backgroundColor: '#4C43A3', opacity: 1 },
                                },
                            },
                            '& .MuiSwitch-thumb': { width: 16, height: 16 },
                            '& .MuiSwitch-track': { borderRadius: 20, backgroundColor: '#D1D5DB', opacity: 1 },
                        }}
                    />
                    <span style={{ textTransform: 'capitalize' }}>{params.value}</span>
                </Stack>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Tooltip title="Copy">
                        <IconButton onClick={() => handleCopy(params.row.fullKey)} color="primary">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {isPermitted('delete') ? (
                        <Tooltip title="Delete">
                            <IconButton onClick={() => handleDeleteKey(params.row.id)} color="error">
                                <DeleteForeverOutlined />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </div>
            ),
        },
    ];

    if (loading && !apiKeys.length) {
        return (
            <div className="row justify-content-center position-relative">
                <div className="col-2 text-center">
                    <Loader isLoading={loading} />
                </div>
            </div>
        );
    }

    if (error) return <div className="alert alert-danger text-center">{error}</div>;

    const handleSortModelChange = async (sortOption) => {
        if (isEmpty(sortOption)) {
            setSortModel(null);
            return;
        }

        const { field, sort } = first(sortOption);
        setSortModel({ field, sort });
    };

    return (
        <>
            <div className="custom_table_container ">
                <div className="table_filters">
                    <h6>APIs</h6>
                    <div className="filter_box">
                        <TextField
                            size="small"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPaginationModel((prev) => ({ ...prev, page: 0 }));
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <span className="material-symbols-rounded">search</span>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        {isPermitted('add') ? (
                            <Button variant="contained" startIcon={<Add />} onClick={() => setOpenApi(true)}>
                                Create API
                            </Button>
                        ) : null}
                    </div>
                </div>

                <div className="custom_table_responsive ">
                    <DataGrid
                        rows={apirows}
                        columns={apicolumns}
                        getRowId={(row) => row.id}
                        autoHeight
                        disableColumnMenu
                        disableRowSelectionOnClick
                        rowHeight={52}
                        loading={loading}
                        localeText={{ noRowsLabel: 'No API Keys Found' }}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                        slots={{
                            pagination: () => (
                                <TablePagination
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    totalCount={totalCount}
                                    pageSizeOptions={[5, 10, 25, 50]}
                                />
                            ),
                        }}
                    />
                </div>
            </div>

            <CreateAPIDialog openApi={openApi} onClose={() => setOpenApi(false)} onSuccess={fetchKeys} />

            <ConfirmationDialog
                open={confirmDialog.open}
                onClose={() =>
                    setConfirmDialog({
                        open: false,
                        type: 'warning',
                        title: '',
                        message: '',
                        onConfirm: null,
                        loading: false,
                    })
                }
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                type={confirmDialog.type}
                loading={confirmDialog.loading}
                confirmText={confirmDialog.type === 'danger' ? 'Delete' : 'Yes, Show Key'}
            />
        </>
    );
};

export default DashboardAPIKeys;
